import type { Address } from '../types';

export type AddressFormatStyle = 'short' | 'full' | 'city_country';

/**
 * Format an address for display.
 *
 * @param address - The address object to format
 * @param style - The format style:
 *   - 'short': city, country
 *   - 'city_country': city, region, country
 *   - 'full': street, zip city, country (French style) - falls back to unparsed_address
 * @returns Formatted address string or empty string if no address
 */
export function formatAddress(
  address: Address | undefined,
  style: AddressFormatStyle = 'full'
): string {
  if (!address) return '';

  const { street, city, zip_code, country, administrative_region_1, administrative_region_2, unparsed_address } =
    address;

  switch (style) {
    case 'short':
      return [city, country].filter(Boolean).join(', ') || unparsed_address || '';

    case 'city_country':
      return [city, administrative_region_1 || administrative_region_2, country]
        .filter(Boolean)
        .join(', ') || unparsed_address || '';

    case 'full':
    default:
      // French-style format: street, zip city, country
      const zipCity = [zip_code, city].filter(Boolean).join(' ');
      const formattedAddress = [street, zipCity, country].filter(Boolean).join(', ');
      // Fall back to unparsed_address if no structured fields
      return formattedAddress || unparsed_address || '';
  }
}

/**
 * Check if an address has any non-empty fields.
 */
export function hasAddress(address: Address | undefined): boolean {
  if (!address) return false;
  return Object.values(address).some((v) => v !== undefined && v !== null && v !== '');
}

/**
 * Create an empty address object.
 */
export function emptyAddress(): Address {
  return {
    country: undefined,
    city: undefined,
    zip_code: undefined,
    administrative_region_1: undefined,
    administrative_region_2: undefined,
    street: undefined,
    unparsed_address: undefined,
  };
}

/**
 * Clean an address object by removing empty string values.
 * Returns undefined if all fields are empty.
 */
export function cleanAddress(address: Address | undefined): Address | undefined {
  if (!address) return undefined;

  const cleaned: Address = {};
  let hasValue = false;

  for (const [key, value] of Object.entries(address)) {
    if (value !== undefined && value !== null && value !== '') {
      cleaned[key as keyof Address] = value;
      hasValue = true;
    }
  }

  return hasValue ? cleaned : undefined;
}
